/**
 * Phone number sanitization utilities.
 * Handles cleaning, country code detection, and standardization.
 */

// Country ISO code → phone dial code mapping
const COUNTRY_PHONE_CODES = {
    il: "972",
    ps: "970",
    jo: "962",
    sa: "966",
    ae: "971",
    eg: "20",
    lb: "961",
    sy: "963",
    iq: "964",
    kw: "965",
    bh: "973",
    qa: "974",
    om: "968",
    ye: "967",
    ma: "212",
    tn: "216",
    dz: "213",
    ly: "218",
    sd: "249",
    tr: "90",
    us: "1",
    gb: "44",
    fr: "33",
    de: "49",
    it: "39",
    es: "34",
    nl: "31",
    se: "46",
    no: "47",
    dk: "45",
    fi: "358",
    ru: "7",
    cn: "86",
    in: "91",
    au: "61",
    br: "55",
    ca: "1",
    mx: "52",
};

/**
 * Strip all non-digit characters from a phone string.
 * @param {string} phone 
 * @returns {string} digits only
 */
export const cleanPhoneNumber = (phone) => {
    if (!phone) return "";
    return phone.replace(/\D/g, "");
};

/**
 * Check if a phone string has a minimum valid length.
 * @param {string} phone - raw or cleaned phone string
 * @returns {boolean}
 */
export const hasValidPhone = (phone) => {
    const cleaned = cleanPhoneNumber(phone);
    return cleaned.length >= 7;
};

/**
 * Get phone number with proper country code for WhatsApp/SMS use.
 * 
 * Case 1: User from "il", phone has no country key → prepend "972"
 * Case 2: User from "il", phone starts with a different country code (e.g., "1" for US) → use as-is
 * Case 3: User from "il", phone already starts with "972" → use as-is
 * 
 * @param {string} phone - raw phone number (may have dashes, spaces, plus sign)
 * @param {string} countryCode - user's country ISO code (e.g., "il", "us")
 * @returns {string} cleaned phone with country code
 */
export const getPhoneWithCountryCode = (phone, countryCode) => {
    const cleaned = cleanPhoneNumber(phone);
    if (!cleaned || cleaned.length < 7) return cleaned;

    const userCountry = (countryCode || "il").toLowerCase();
    const userDialCode = COUNTRY_PHONE_CODES[userCountry] || "";

    if (!userDialCode) {
        // Unknown country, return cleaned phone as-is
        return cleaned;
    }

    // Case 3: Phone already starts with the user's country dial code
    if (cleaned.startsWith(userDialCode)) {
        return cleaned;
    }

    // Case 2: Check if phone starts with any OTHER known country code
    // Sort codes longest first to avoid partial matches (e.g., "1" matching before "972")
    const allCodes = Object.values(COUNTRY_PHONE_CODES);
    const sortedCodes = [...new Set(allCodes)].sort((a, b) => b.length - a.length);

    for (const code of sortedCodes) {
        if (cleaned.startsWith(code) && cleaned.length > code.length + 5) {
            // Phone starts with a different valid country code → use as-is
            return cleaned;
        }
    }

    // Case 1: No country code detected → prepend the user's country dial code
    // Remove leading zero if present (common in local numbers)
    const withoutLeadingZero = cleaned.startsWith("0") ? cleaned.substring(1) : cleaned;
    return userDialCode + withoutLeadingZero;
};

/**
 * Get the country phone dial code from ISO country code.
 * @param {string} countryCode - ISO country code (e.g., "il")
 * @returns {string} dial code (e.g., "972")
 */
export const getDialCode = (countryCode) => {
    if (!countryCode) return "";
    return COUNTRY_PHONE_CODES[countryCode.toLowerCase()] || "";
};
